import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar, { type SortKey } from "@/pages/category/components/FilterBar";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type = "" } = useParams();
  const current = categories.find((c) => c.key === type);

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [minRating, setMinRating] = useState("");
  const [sort, setSort] = useState<SortKey>("year");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    setSelectedGenres(type && categories.some((c) => c.key === type) ? [type] : []);
    setYearFrom("");
    setYearTo("");
    setMinRating("");
    setSort("year");
    setVisible(PAGE_SIZE);
  }, [type]);

  useSeo({
    title: current
      ? `${current.name}电影推荐 - 欧美大片影视网`
      : "全部电影 - 欧美大片影视网",
    description: current
      ? `${current.desc} 欧美大片影视网为你精选${current.name}类型欧美电影，附评分与影评摘要，助你快速找到想看的影片。`
      : "欧美大片影视网收录全部欧美电影，支持按类型、年份与评分筛选，帮助你发现更多好片。",
    keywords: current
      ? `${current.name}电影,${current.name}片推荐,欧美电影,电影推荐`
      : "欧美电影,全部电影,电影筛选,电影推荐",
  });

  const filtered = useMemo(() => {
    let list = movies.filter((m) => {
      if (selectedGenres.length && !m.genres.some((g) => selectedGenres.includes(g)))
        return false;
      if (yearFrom && m.year < Number(yearFrom)) return false;
      if (yearTo && m.year > Number(yearTo)) return false;
      if (minRating && m.rating < Number(minRating)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "popularity") return b.popularity - a.popularity;
      return b.year - a.year || b.rating - a.rating;
    });
    return list;
  }, [selectedGenres, yearFrom, yearTo, minRating, sort]);

  const shown = filtered.slice(0, visible);

  const toggleGenre = (key: string) =>
    setSelectedGenres((prev) =>
      prev.includes(key) ? prev.filter((g) => g !== key) : [...prev, key],
    );

  const handleReset = () => {
    setSelectedGenres(type && categories.some((c) => c.key === type) ? [type] : []);
    setYearFrom("");
    setYearTo("");
    setMinRating("");
    setSort("year");
    setVisible(PAGE_SIZE);
  };

  return (
    <SiteLayout>
      {/* header */}
      <section className="w-full bg-primary-950 text-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500 text-primary-950">
              <i className={`${current ? current.icon : "ri-apps-2-line"} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading font-extrabold text-2xl md:text-3xl">
                {current ? `${current.name}电影推荐` : "全部电影"}
              </h1>
              <p className="font-label text-xs tracking-wider text-accent-400 uppercase mt-0.5">
                {current ? current.en : "All Movies"}
              </p>
            </div>
          </div>
          <p className="text-sm text-background-300/90 max-w-3xl leading-relaxed">
            {current
              ? current.desc
              : "浏览欧美大片影视网收录的全部电影，使用下方筛选与排序工具，快速定位你想看的作品。"}
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类", to: "/category/action" },
            { label: current ? `${current.name}电影` : "全部电影" },
          ]}
        />

        <h2 className="sr-only">筛选与电影列表</h2>

        <div className="mt-5 md:mt-7">
          <FilterBar
            selectedGenres={selectedGenres}
            toggleGenre={toggleGenre}
            yearFrom={yearFrom}
            yearTo={yearTo}
            setYearFrom={setYearFrom}
            setYearTo={setYearTo}
            minRating={minRating}
            setMinRating={setMinRating}
            sort={sort}
            setSort={setSort}
            onReset={handleReset}
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
          />
        </div>

        <div className="mt-5 flex items-center justify-between text-sm text-foreground-500">
          <span>
            共找到 <strong className="text-primary-600">{filtered.length}</strong> 部影片
          </span>
          <span className="hidden sm:block">
            已显示 {shown.length} / {filtered.length}
          </span>
        </div>

        {/* list */}
        {filtered.length === 0 ? (
          <div className="mt-8 rounded-lg bg-background-100 border border-background-200 p-10 md:p-16 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-search-eye-line text-3xl" />
            </div>
            <h3 className="font-heading font-bold text-lg text-foreground-800 mb-2">
              没有找到符合条件的电影
            </h3>
            <p className="text-sm text-foreground-500 mb-6">
              试试放宽筛选条件，或重置后重新浏览全部影片。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" /> 重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {shown.map((movie) => (
                <MovieCard
                  key={movie.id}
                  movie={movie}
                  showExcerpt
                  isFavorite={isFavorite(movie.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>

            {visible < filtered.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  加载更多影片
                  <i className="ri-arrow-down-line" />
                </button>
              </div>
            )}
            {visible >= filtered.length && filtered.length > PAGE_SIZE && (
              <p className="mt-8 text-center text-sm text-foreground-400">
                已经到底啦，共 {filtered.length} 部影片
              </p>
            )}
          </>
        )}

        {/* other categories */}
        <div className="mt-12 pt-8 border-t border-background-200">
          <h2 className="font-heading font-bold text-lg text-foreground-900 mb-4">
            浏览其他分类
          </h2>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={`/category/${cat.key}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  cat.key === type
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 border border-background-200 text-foreground-600 hover:border-primary-300"
                }`}
              >
                {cat.name}电影
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}